// NOTE: The report IDs and titles are duplicated in
// apps/admin/src/modules/settings/reportTypes.ts for use in the admin app.
// If you add or remove a report type, update both files to keep them in sync.

export interface ReportType {
	id: string
	title: string
	description?: string
	requiresCustomField?: string
	supportsMultiCycle?: boolean
}

export interface CustomFieldFilter {
	field: string
	values: string[]
}

export interface ExcelExportOutput {
	filename: string
	data: string
	contentType: string
}

export interface Signature {
	signedBy: string
	signedAt: Date
	role: string
}

export const REPORT_TYPES: Record<string, ReportType[]> = {
	'Overall Cycle Status Reports': [
		{ id: 'overall-status', title: 'Overall Status', supportsMultiCycle: true },
		{ id: 'status-by-reviewer', title: 'Status by Reviewer' },
	],
	'Full Evaluation Reports': [
		{
			id: 'full-evaluation-report',
			title: 'Full Evaluation Report',
			description: 'One row per employee with manager responses as columns.',
		},
	],
	'Incomplete Status Reports': [
		{ id: 'not-submitted-all', title: 'Not Submitted - All' },
		{ id: 'not-submitted-employee', title: 'Not Submitted - Employee' },
		{ id: 'not-submitted-manager', title: 'Not Submitted - Manager' },
	],
	'Status Aggregate Reports': [
		{
			id: 'individual-by-department',
			title: 'Individual Status by Department',
			requiresCustomField: 'department',
		},
		{
			id: 'individual-by-title',
			title: 'Individual Status by Title',
			requiresCustomField: 'title',
		},
		{
			id: 'individual-by-location',
			title: 'Individual Status by Location',
			requiresCustomField: 'location',
		},
	],
	Ratings: [
		{ id: 'pec-ratings', title: 'PEC Ratings' },
		{ id: 'all-ratings', title: 'All Ratings' },
		{ id: 'competency-ratings-manager', title: 'Competency Ratings - Manager' },
		{ id: 'overall-ratings-manager', title: 'Overall Ratings - Manager' },
		{ id: 'individual-competency-ratings', title: 'Individual Competency Ratings' },
	],
	Comments: [
		{ id: 'all-comments', title: 'All Comments' },
		{ id: 'flagged-words', title: 'Flagged Words' },
	],
	'Distributions & Comparisons': [
		{ id: 'competency-average-ratings', title: 'Competency Average Ratings' },
		{
			id: 'overall-rating-comparison-department',
			title: 'Overall Rating - Comparison by Department',
			requiresCustomField: 'department',
		},
		{
			id: 'overall-rating-comparison-title',
			title: 'Overall Rating - Comparison by Title',
			requiresCustomField: 'title',
		},
		{
			id: 'overall-rating-comparison-location',
			title: 'Overall Rating - Comparison by Location',
			requiresCustomField: 'location',
		},
		{
			id: 'avg-competency-ratings-comparison-department',
			title: 'Average Competency Ratings - Comparison by Department',
			requiresCustomField: 'department',
		},
		{
			id: 'avg-competency-ratings-comparison-title',
			title: 'Average Competency Ratings - Comparison by Title',
			requiresCustomField: 'title',
		},
		{
			id: 'avg-competency-ratings-comparison-location',
			title: 'Average Competency Ratings - Comparison by Location',
			requiresCustomField: 'location',
		},
	],
}

export const findReportById = (reportId: string): ReportType | null => {
	for (const reports of Object.values(REPORT_TYPES)) {
		const report = reports.find((r) => r.id === reportId)
		if (report) return report
	}
	return null
}

export const getAllReports = (): ReportType[] => {
	return Object.values(REPORT_TYPES).flat()
}
